<template>
  <base-modal-small @close="emit('close')">
    <template #header>
      <span class="text-base font-semibold pl-1">
        {{ props.onBehalfOf ? `New request for ${props.onBehalfOfLabel}` : 'New request' }}
      </span>
    </template>

    <template #body>
      <div class="w-full p-3 overflow-auto">
        <!-- Said again here, not only on the page behind: this is the box
             the words go in, and it is the last point at which somebody can
             notice the name on them is not theirs. -->
        <p
          v-if="props.onBehalfOf"
          class="text-sm rounded-md border border-amber-600 bg-amber-50 text-amber-900
                 px-2 py-1.5 mb-2"
        >
          Filing as {{ props.onBehalfOfLabel }}. Write it in their words — it
          lands on their page under their name.
        </p>

        <textarea
          ref="bodyInput"
          v-model="body"
          rows="5"
          class="w-full border border-gray-400 rounded-md p-2 text-sm"
          :placeholder="props.onBehalfOf
            ? `What did ${props.onBehalfOfLabel} ask for?`
            : 'What do you need? Describe it however makes sense to you.'"
        ></textarea>

        <!-- Files are held here and uploaded after submit: an attachment needs
             a request to hang off, and that id does not exist until the server
             has made one. -->
        <div class="mt-2">
          <label class="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              ref="filePicker"
              type="file"
              multiple
              :accept="ATTACH_ACCEPT"
              class="hidden"
              @change="holdFiles"
            />
            <span class="px-2 py-1 border border-gray-400 rounded-md text-xs hover:bg-gray-100">
              Add a screenshot or file
            </span>
            <span v-if="pendingFiles.length" class="text-xs text-gray-600">
              {{ pendingFiles.length }} ready to send
              <button class="underline ml-1" @click.prevent="clearFiles">clear</button>
            </span>
            <span v-else class="text-xs text-gray-500">
              PNG, JPEG, HEIC, GIF, WebP or PDF
            </span>
          </label>
        </div>

        <div class="flex items-center gap-3 mt-3">
          <button
            class="px-4 py-1.5 rounded-md text-sm text-white bg-accent-primary
                   hover:bg-accent-secondary disabled:opacity-50"
            :disabled="!body.trim() || submitting"
            @click="submit"
          >
            {{ submitting
              ? 'Sending…'
              : (props.onBehalfOf ? `Submit as ${props.onBehalfOfLabel}` : 'Submit') }}
          </button>
          <button
            class="text-sm text-gray-600 underline"
            :disabled="submitting"
            @click="emit('close')"
          >
            Cancel
          </button>
        </div>

        <p v-if="submitError" class="text-sm text-red-600 mt-2">{{ submitError }}</p>
      </div>
    </template>
  </base-modal-small>
</template>

<script setup>
import { defineEmits, defineProps, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { submitRequest, uploadAttachment } from '@/services/BackOffice'

const emit = defineEmits(['close', 'created'])

// A user id, or null for the ordinary case of filing your own. Declared with
// types rather than as an array so `on-behalf-of` cannot arrive as the empty
// string and read as truthy.
const props = defineProps({
  onBehalfOf: { type: Number, default: null },
  onBehalfOfLabel: { type: String, default: '' },
})

const store = useStore()
const token = () => store.getters['auth/showToken']

const body = ref('')
const submitting = ref(false)
const submitError = ref('')
const pendingFiles = ref([])
const filePicker = ref(null)
const bodyInput = ref(null)

// Mirrors Attachment.ALLOWED_MIME_TYPES; the server remains the authority.
const ATTACH_ACCEPT = 'image/png,image/jpeg,image/heic,image/heif,image/webp,image/gif,application/pdf'

function holdFiles(event) {
  pendingFiles.value = Array.from(event.target.files || [])
}

function clearFiles() {
  pendingFiles.value = []
  if (filePicker.value) filePicker.value.value = ''
}

async function submit() {
  submitting.value = true
  submitError.value = ''
  try {
    const created = await submitRequest(
      token(), body.value.trim(), props.onBehalfOf,
    )
    // Uploaded after the request exists. A failure here must not read as a
    // failed submission -- the request is already filed, so we report which
    // half went wrong and still close, because submitting again would file
    // the same request twice.
    let warning = ''
    if (pendingFiles.value.length && created && created.id) {
      try {
        for (const file of pendingFiles.value) {
          await uploadAttachment(token(), 'request', created.id, file)
        }
      } catch (e) {
        warning = `Request filed, but a file did not upload: ${describe(e)}`
      }
    }
    emit('created', warning)
  } catch (e) {
    submitError.value = describe(e)
  } finally {
    submitting.value = false
  }
}

function describe(e) {
  if (e?.response?.status === 403) {
    return 'Back Office requests are not open yet.'
  }
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

onMounted(() => {
  if (bodyInput.value) bodyInput.value.focus()
})
</script>
